import React, { useState } from 'react';
import styled from 'styled-components';
import { Shield, Server, Zap, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import emailjs from '@emailjs/browser';

const PageContainer = styled.div`
    width: 100%;
    min-height: 100vh;
    background: linear-gradient(135deg, #020611 0%, #050B15 50%, #020611 100%);
    color: white;
    font-family: Very Sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    position: relative;
    overflow-x: hidden;
    display: flex;
    align-items: center;
    padding: 3rem 0;
`;

const GradientOrb1 = styled.div`
    position: absolute;
    top: -10%;
    left: -15%;
    width: 700px;
    height: 700px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
`;

const GradientOrb2 = styled.div`
    position: absolute;
    bottom: -10%;
    right: -15%;
    width: 800px;
    height: 800px;
    background: radial-gradient(circle, rgba(139, 92, 246, 0.08) 0%, transparent 70%);
    pointer-events: none;
    z-index: 1;
`;

const ContentWrapper = styled.div`
    position: relative;
    z-index: 2;
    width: 80%;
    max-width: 1400px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1.2fr;
    gap: 3rem;
    align-items: start;
    
    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
        gap: 2rem;
    }
`;

const LeftSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    position: sticky;
    top: 2rem;
    @media screen and (max-width: 768px) {
    position: static;
    }
`;

const Title = styled.h1`
    font-family: Very Sans Headline, Sans Serif;
    font-size: 46px;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
    line-height: 1.2;
    
    .highlight {
        background: linear-gradient(135deg, #3B82F6 0%, #a78bfa 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
`;

const Description = styled.p`
    color: #94A3B8;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.7;
    margin: 0;
`;

const InfoBox = styled.div`
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 12px;
    padding: 1.5rem;
`;

const InfoTitle = styled.h3`
    font-family: Very Sans Bold, Sans Serif;
    font-size: 18px;
    color: #ffffff;
    margin: 0 0 1rem 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const InfoText = styled.p`
    color: #94A3B8;
    font-size: 14px;
    line-height: 1.6;
    margin: 0;
`;

const RequirementsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 1rem;
`;

const RequirementItem = styled.div`
    display: flex;
    align-items: start;
    gap: 0.75rem;
    color: #94A3B8;
    font-size: 14px;
    line-height: 1.5;
`;

const RightSection = styled.div`
    background: rgba(255, 255, 255, 0.025);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 16px;
    padding: 2.5rem;
    @media screen and (max-width: 768px) {
    margin 20px auto;

    }
`;

const FormTitle = styled.h2`
    font-family: Very Sans Bold, Sans Serif;
    font-size: 28px;
    color: #ffffff;
    margin: 0 0 0.5rem 0;
`;

const FormSubtitle = styled.p`
    color: #94A3B8;
    font-size: 14px;
    margin: 0 0 2rem 0;
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
`;

const FormGroup = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
`;

const Label = styled.label`
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    font-family: Very Sans, Sans Serif;
`;

const Input = styled.input`
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 12px 16px;
    color: #ffffff;
    font-size: 14px;
    font-family: Very Sans, Sans Serif;
    outline: none;
    transition: all 0.3s ease;
    
    &::placeholder {
        color: #94A3B8;
    }
    
    &:focus {
        border-color: rgba(59, 130, 246, 0.5);
        background: rgba(0, 0, 0, 0.3);
    }
`;

const TextArea = styled.textarea`
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 12px 16px;
    color: #ffffff;
    font-size: 14px;
    font-family: Very Sans, Sans Serif;
    outline: none;
    transition: all 0.3s ease;
    min-height: 100px;
    resize: vertical;
    
    &::placeholder {
        color: #94A3B8;
    }
    
    &:focus {
        border-color: rgba(59, 130, 246, 0.5);
        background: rgba(0, 0, 0, 0.3);
    }
`;

const GridRow = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const SubmitButton = styled.button`
    background: #3B82F6;
    color: white;
    border: none;
    padding: 14px 32px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: Very Sans, Sans Serif;
    margin-top: 1rem;
    box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    
    &:hover:not(:disabled) {
        background: #2563EB;
        transform: translateY(-2px);
        box-shadow: 0 6px 25px rgba(59, 130, 246, 0.4);
    }
    
    &:active:not(:disabled) {
        transform: translateY(0);
    }
    
    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
    
    svg {
        animation: ${props => props.$isLoading ? 'spin 1s linear infinite' : 'none'};
    }
    
    @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
`;

const StatusMessage = styled.div`
    border-radius: 12px;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    text-align: center;
    margin-bottom: 1.5rem;
    animation: slideIn 0.4s ease;
    
    @keyframes slideIn {
        from {
            opacity: 0;
            transform: scale(0.95) translateY(-10px);
        }
        to {
            opacity: 1;
            transform: scale(1) translateY(0);
        }
    }
`;

const SuccessMessage = styled(StatusMessage)`
    background: rgba(34, 197, 94, 0.15);
    border: 2px solid rgba(34, 197, 94, 0.5);
`;

const ErrorMessage = styled(StatusMessage)`
    background: rgba(239, 68, 68, 0.15);
    border: 2px solid rgba(239, 68, 68, 0.5);
`;

const StatusIcon = styled.div`
    width: 72px;
    height: 72px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${props => props.$type === 'success' 
        ? 'rgba(34, 197, 94, 0.2)' 
        : 'rgba(239, 68, 68, 0.2)'};
`;

const StatusTitle = styled.div`
    font-size: 22px;
    font-weight: 600;
    font-family: Very Sans Bold, Sans Serif;
    color: ${props => props.$type === 'success' ? '#22C55E' : '#EF4444'};
`;

const StatusText = styled.div`
    color: #94A3B8;
    font-size: 14px;
    font-family: Very Sans, Sans Serif;
    line-height: 1.6;
    max-width: 400px;
`;

const ValidatorApp = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        organization: '',
        country: '',
        website: '',
        serverSpecs: '',
        experience: '',
        motivation: '',
        twitterHandle: '',
        telegramHandle: ''
    });

    const [submitStatus, setSubmitStatus] = useState(null); // null, 'loading', 'success', 'error'

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitStatus('loading');
        
        try {
            await emailjs.send(
                process.env.REACT_APP_EMAIL_SERVICE_ID,
                process.env.REACT_APP_EMAIL_TEMPLATE_ID,
                {
                    from_name: formData.fullName,
                    from_email: formData.email,
                    organization: formData.organization || 'N/A',
                    country: formData.country,
                    website: formData.website || 'N/A',
                    twitter: formData.twitterHandle || 'N/A',
                    telegram: formData.telegramHandle || 'N/A',
                    server_specs: formData.serverSpecs,
                    experience: formData.experience,
                    motivation: formData.motivation
                },
                process.env.REACT_APP_EMAIL_PUBLIC_KEY
            );
            
            setSubmitStatus('success');
            
            // Reset form after 5 seconds
            setTimeout(() => {
                setFormData({
                    fullName: '', email: '', organization: '',
                    country: '', website: '', serverSpecs: '',
                    experience: '', motivation: '',
                    twitterHandle: '', telegramHandle: ''
                });
                setSubmitStatus(null);
            }, 5000);
            
        } catch (error) {
            console.error('EmailJS Error:', error);
            setSubmitStatus('error');
            
            // Clear error after 5 seconds
            setTimeout(() => {
                setSubmitStatus(null);
            }, 5000);
        }
    };

    return (
        <PageContainer>
            <GradientOrb1 />
            <GradientOrb2 />
            <ContentWrapper>
                <LeftSection>
                    <Title>
                        Become a <span className="highlight">Validator</span>
                    </Title>
                    
                    <Description>
                        Join the Hydrix network as a trusted validator and help secure the blockchain 
                        while earning rewards. Our Proof of Authority (PoA) consensus ensures fast, 
                        efficient, and reliable transaction validation.
                    </Description>
                    
                    <InfoBox>
                        <InfoTitle>
                            <Shield size={20} color="#3B82F6" />
                            What is Proof of Authority?
                        </InfoTitle>
                        <InfoText>
                            Hydrix uses a Proof of Authority (PoA) consensus mechanism where validators 
                            are pre-approved and trusted entities. This ensures high performance, 
                            low latency, and energy efficiency while maintaining network security through 
                            reputation-based validation.
                        </InfoText>
                    </InfoBox>
                    
                    <div>
                        <InfoTitle>
                            <Server size={20} color="#3B82F6" />
                            Technical Requirements
                        </InfoTitle>
                        <RequirementsList>
                            <RequirementItem>
                                <CheckCircle size={18} color="#3B82F6" style={{ flexShrink: 0, marginTop: '2px' }} />
                                <span><strong>Server:</strong> Dedicated server with 99.9% uptime guarantee</span>
                            </RequirementItem>
                            <RequirementItem>
                                <CheckCircle size={18} color="#3B82F6" style={{ flexShrink: 0, marginTop: '2px' }} />
                                <span><strong>CPU:</strong> 8+ cores (modern processor)</span>
                            </RequirementItem>
                            <RequirementItem>
                                <CheckCircle size={18} color="#3B82F6" style={{ flexShrink: 0, marginTop: '2px' }} />
                                <span><strong>RAM:</strong> 16GB+ memory</span>
                            </RequirementItem>
                            <RequirementItem>
                                <CheckCircle size={18} color="#3B82F6" style={{ flexShrink: 0, marginTop: '2px' }} />
                                <span><strong>Storage:</strong> 500GB+ SSD</span>
                            </RequirementItem>
                            <RequirementItem>
                                <CheckCircle size={18} color="#3B82F6" style={{ flexShrink: 0, marginTop: '2px' }} />
                                <span><strong>Network:</strong> 100 Mbps+ connection</span>
                            </RequirementItem>
                            <RequirementItem>
                                <CheckCircle size={18} color="#3B82F6" style={{ flexShrink: 0, marginTop: '2px' }} />
                                <span><strong>Experience:</strong> Blockchain infrastructure management</span>
                            </RequirementItem>
                        </RequirementsList>
                    </div>
                    
                    <InfoBox>
                        <InfoTitle>
                            <Zap size={20} color="#3B82F6" />
                            Validator Benefits
                        </InfoTitle>
                        <InfoText>
                            Earn HYX rewards for validating transactions, gain early access to network 
                            upgrades, and become part of Hydrix's core infrastructure with recognition 
                            in the community.
                        </InfoText>
                    </InfoBox>
                </LeftSection>

                <RightSection>
                    <FormTitle>Validator Application</FormTitle>
                    <FormSubtitle>
                        Fill out the form below to apply. Our team will review your application and contact you within 5-7 business days.
                    </FormSubtitle>
                    
                    {submitStatus === 'success' && (
                        <SuccessMessage>
                            <StatusIcon $type="success">
                                <CheckCircle size={36} color="#22C55E" />
                            </StatusIcon>
                            <StatusTitle $type="success">Application Submitted!</StatusTitle>
                            <StatusText>
                                Thank you for your interest in becoming a Hydrix validator. 
                                We've received your application and will review it carefully. 
                                Expect to hear from us within 5-7 business days.
                            </StatusText>
                        </SuccessMessage>
                    )}
                    
                    {submitStatus === 'error' && (
                        <ErrorMessage>
                            <StatusIcon $type="error">
                                <AlertCircle size={36} color="#EF4444" />
                            </StatusIcon>
                            <StatusTitle $type="error">Submission Failed</StatusTitle>
                            <StatusText>
                                Something went wrong while submitting your application. 
                                Please check your internet connection and try again. 
                                If the problem persists, contact us directly.
                            </StatusText>
                        </ErrorMessage>
                    )}
                    
                    <Form onSubmit={handleSubmit}>
                        <GridRow>
                            <FormGroup>
                                <Label>Full Name *</Label>
                                <Input
                                    type="text"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    placeholder="John Doe"
                                    required
                                    disabled={submitStatus === 'loading' || submitStatus === 'success'}
                                />
                            </FormGroup>
                            
                            <FormGroup>
                                <Label>Email *</Label>
                                <Input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="john@example.com"
                                    required
                                    disabled={submitStatus === 'loading' || submitStatus === 'success'}
                                />
                            </FormGroup>
                        </GridRow>
                        
                        <GridRow>
                            <FormGroup>
                                <Label>Organization</Label>
                                <Input
                                    type="text"
                                    name="organization"
                                    value={formData.organization}
                                    onChange={handleChange}
                                    placeholder="Company or Entity"
                                    disabled={submitStatus === 'loading' || submitStatus === 'success'}
                                />
                            </FormGroup>
                            
                            <FormGroup>
                                <Label>Country *</Label>
                                <Input
                                    type="text"
                                    name="country"
                                    value={formData.country}
                                    onChange={handleChange}
                                    placeholder="United States"
                                    required
                                    disabled={submitStatus === 'loading' || submitStatus === 'success'}
                                />
                            </FormGroup>
                        </GridRow>
                        
                        <FormGroup>
                            <Label>Website / LinkedIn</Label>
                            <Input
                                type="url"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                placeholder="https://example.com"
                                disabled={submitStatus === 'loading' || submitStatus === 'success'}
                            />
                        </FormGroup>
                        
                        <GridRow>
                            <FormGroup>
                                <Label>Twitter Handle</Label>
                                <Input
                                    type="text"
                                    name="twitterHandle"
                                    value={formData.twitterHandle}
                                    onChange={handleChange}
                                    placeholder="@username"
                                    disabled={submitStatus === 'loading' || submitStatus === 'success'}
                                />
                            </FormGroup>
                            
                            <FormGroup>
                                <Label>Telegram Handle</Label>
                                <Input
                                    type="text"
                                    name="telegramHandle"
                                    value={formData.telegramHandle}
                                    onChange={handleChange}
                                    placeholder="@username"
                                    disabled={submitStatus === 'loading' || submitStatus === 'success'}
                                />
                            </FormGroup>
                        </GridRow>
                        
                        <FormGroup>
                            <Label>Server Specifications *</Label>
                            <TextArea
                                name="serverSpecs"
                                value={formData.serverSpecs}
                                onChange={handleChange}
                                placeholder="Describe your server setup (CPU, RAM, Storage, Network, Provider)"
                                required
                                disabled={submitStatus === 'loading' || submitStatus === 'success'}
                            />
                        </FormGroup>
                        
                        <FormGroup>
                            <Label>Blockchain Experience *</Label>
                            <TextArea
                                name="experience"
                                value={formData.experience}
                                onChange={handleChange}
                                placeholder="Describe your experience running blockchain nodes or validators"
                                required
                                disabled={submitStatus === 'loading' || submitStatus === 'success'}
                            />
                        </FormGroup>
                        
                        <FormGroup>
                            <Label>Why do you want to become a Hydrix validator? *</Label>
                            <TextArea
                                name="motivation"
                                value={formData.motivation}
                                onChange={handleChange}
                                placeholder="Tell us why you're interested in becoming a validator"
                                required
                                disabled={submitStatus === 'loading' || submitStatus === 'success'}
                            />
                        </FormGroup>
                        
                        <SubmitButton 
                            type="submit" 
                            disabled={submitStatus === 'loading' || submitStatus === 'success'}
                            $isLoading={submitStatus === 'loading'}
                        >
                            {submitStatus === 'loading' ? (
                                <>
                                    <Loader size={20} />
                                    Submitting...
                                </>
                            ) : (
                                'Submit Application'
                            )}
                        </SubmitButton>
                    </Form>
                </RightSection>
            </ContentWrapper>
        </PageContainer>
    );
};

export default ValidatorApp;